import React from 'react';

import { useIntl } from 'react-intl';

import { MembershipIdentifier } from 'src/features/membership/types';
import { identifiersMessages } from 'src/features/membership/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useUpdateIdentifier } from 'src/features/membership/api/identifier/useUpdateIdentifier';
import IdentifierFormModal from 'src/features/membership/components/forms/identifier/IdentifierFormModal';
import { LIST_IDENTIFIERS_RESOURCE } from 'src/features/membership/api/identifier/useListIdentifiers';
import { useParking } from 'src/providers/ParkingProvider';

interface IdentifierFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedIdentifier: MembershipIdentifier;
    children?: React.ReactNode;
    membershipId?: string;
    isClient?: boolean;
}

function UpdateIdentifierFormModal<TData>({
    className,
    semantics,
    children,
    selectedIdentifier,
    isClient = false,
    membershipId = '',
}: IdentifierFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const { mutate, isPending } = useUpdateIdentifier({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_IDENTIFIERS_RESOURCE] });
            toast({
                title: formatMessage(identifiersMessages.updateIdentifierSuccess),
                description: formatMessage(identifiersMessages.updateIdentifierSuccessDescription, {
                    entry: success.identifier.value,
                }),
            });
        },
    });

    return (
        <IdentifierFormModal
            selectedIdentifier={selectedIdentifier}
            className={className}
            mutate={(data, onSuccess, onError, isValidated) =>
                mutate({ data, identifierId: selectedIdentifier.id, membershipId, parkingId, is_validated: isValidated }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(identifiersMessages.editIdentifierSubtitle, {
                entry: selectedIdentifier.identifier.value,
            })}
            isClient={isClient}
        >
            {children}
        </IdentifierFormModal>
    );
}

export default UpdateIdentifierFormModal;
